import { CanvasItemKind, ItemLock, ItemLockRef, StickyState, TextLabelState } from '../../../api/Requests';
import { diffCanvasRestore } from '../../../utils/canvasUtils';

export function occupancyKey(kind: CanvasItemKind, itemId: string): string {
  return `${kind}:${itemId}`;
}

export type OccupancyMap = Map<string, ItemLock>;

export function applyLocksSnapshot(locks: ItemLock[] | null | undefined): OccupancyMap {
  const next: OccupancyMap = new Map();
  (locks ?? []).forEach(lock => {
    if (!lock?.kind || !lock.itemId) return;
    next.set(occupancyKey(lock.kind, lock.itemId), lock);
  });
  return next;
}

export function applyLocksChanged(
  current: OccupancyMap,
  changed: ItemLocksChangedLike,
): OccupancyMap {
  const next = new Map(current);
  (changed.released ?? []).forEach(ref => {
    if (!ref?.kind || !ref.itemId) return;
    next.delete(occupancyKey(ref.kind, ref.itemId));
  });
  (changed.acquired ?? []).forEach(lock => {
    if (!lock?.kind || !lock.itemId) return;
    next.set(occupancyKey(lock.kind, lock.itemId), lock);
  });
  return next;
}

export interface ItemLocksChangedLike {
  acquired?: ItemLock[] | null;
  released?: ItemLockRef[] | null;
}

export function isOccupiedByOther(
  occupancy: OccupancyMap,
  kind: CanvasItemKind,
  itemId: string,
  localPlayerId: string,
): boolean {
  const lock = occupancy.get(occupancyKey(kind, itemId));
  return !!lock && lock.playerId !== localPlayerId;
}

export function isOccupiedByMe(
  occupancy: OccupancyMap,
  kind: CanvasItemKind,
  itemId: string,
  localPlayerId: string,
): boolean {
  const lock = occupancy.get(occupancyKey(kind, itemId));
  return !!lock && lock.playerId === localPlayerId;
}

export function occupiedByName(
  occupancy: OccupancyMap,
  kind: CanvasItemKind,
  itemId: string,
  localPlayerId: string,
): string | undefined {
  const lock = occupancy.get(occupancyKey(kind, itemId));
  if (!lock || lock.playerId === localPlayerId) return undefined;
  return lock.playerName;
}

/** True when a restore diff would upsert or delete an item currently locked by someone else. */
export function restoreDiffTouchesForeignLock(
  occupancy: OccupancyMap,
  localPlayerId: string,
  upsertStickyIds: string[],
  upsertLabelIds: string[],
  deleteStickyIds: string[],
  deleteLabelIds: string[],
): boolean {
  const stickyIds = [...upsertStickyIds, ...deleteStickyIds];
  const labelIds = [...upsertLabelIds, ...deleteLabelIds];
  if (stickyIds.some(id => isOccupiedByOther(occupancy, 'sticky', id, localPlayerId))) return true;
  if (labelIds.some(id => isOccupiedByOther(occupancy, 'label', id, localPlayerId))) return true;
  return false;
}

export function restoreTouchesForeignLock(
  occupancy: OccupancyMap,
  localPlayerId: string,
  restoredStickies: StickyState[],
  restoredLabels: TextLabelState[],
  prevStickies: StickyState[],
  prevLabels: TextLabelState[],
): boolean {
  const diff = diffCanvasRestore(restoredStickies, restoredLabels, prevStickies, prevLabels);
  return restoreDiffTouchesForeignLock(
    occupancy,
    localPlayerId,
    diff.upsertStickies.map(s => s.id),
    diff.upsertLabels.map(l => l.id),
    diff.deleteStickyIds,
    diff.deleteLabelIds,
  );
}

export const OCCUPANCY_DENY_TOAST = 'Can’t edit — someone else has those items';

export type PersistWhenHeldDecision = 'run' | 'queue' | 'drop';

export function idsAreHeld(
  heldStickies: Set<string>,
  heldLabels: Set<string>,
  stickyIds: string[],
  labelIds: string[],
): boolean {
  return stickyIds.every(id => heldStickies.has(id))
    && labelIds.every(id => heldLabels.has(id));
}

export function idsAreClaimed(
  heldStickies: Set<string>,
  heldLabels: Set<string>,
  pendingStickies: Set<string>,
  pendingLabels: Set<string>,
  stickyIds: string[],
  labelIds: string[],
): boolean {
  return stickyIds.every(id => heldStickies.has(id) || pendingStickies.has(id))
    && labelIds.every(id => heldLabels.has(id) || pendingLabels.has(id));
}

export function decidePersistWhenHeld(
  heldStickies: Set<string>,
  heldLabels: Set<string>,
  pendingStickies: Set<string>,
  pendingLabels: Set<string>,
  stickyIds: string[],
  labelIds: string[],
): PersistWhenHeldDecision {
  if (idsAreHeld(heldStickies, heldLabels, stickyIds, labelIds)) return 'run';
  if (idsAreClaimed(heldStickies, heldLabels, pendingStickies, pendingLabels, stickyIds, labelIds)) return 'queue';
  return 'drop';
}

export interface AcquireApplyResult {
  granted: boolean;
  isCurrent: boolean;
  /** True when this reply still owns the local session (current, or abandoned by deselect). */
  shouldAbortSession: boolean;
  releaseStickies: string[];
  releaseLabels: string[];
}

/**
 * In-memory occupancy session for one client. Server remains the lock authority;
 * this only tracks held / in-flight ids and a single queued persist.
 */
export function createOccupancyTracker() {
  const heldStickies = new Set<string>();
  const heldLabels = new Set<string>();
  const pendingStickies = new Set<string>();
  const pendingLabels = new Set<string>();
  let gen = 0;
  let lastAcquireGen = 0;
  let pendingPersist: (() => void) | null = null;
  let pendingPersistGen = 0;

  const isClaimed = (kind: 'sticky' | 'label', id: string): boolean => (
    kind === 'sticky'
      ? heldStickies.has(id) || pendingStickies.has(id)
      : heldLabels.has(id) || pendingLabels.has(id)
  );

  const claimed = (stickyIds: string[], labelIds: string[]): boolean =>
    idsAreClaimed(heldStickies, heldLabels, pendingStickies, pendingLabels, stickyIds, labelIds);

  const persistWhenHeld = (stickyIds: string[], labelIds: string[], run: () => void): PersistWhenHeldDecision => {
    const decision = decidePersistWhenHeld(
      heldStickies, heldLabels, pendingStickies, pendingLabels, stickyIds, labelIds,
    );
    if (decision === 'run') run();
    else if (decision === 'queue') {
      pendingPersist = run;
      pendingPersistGen = gen;
    }
    return decision;
  };

  const release = (stickyIds: string[], labelIds: string[]): void => {
    stickyIds.forEach(id => heldStickies.delete(id));
    labelIds.forEach(id => heldLabels.delete(id));
  };

  const beginAcquire = (toAcquireStickies: string[], toAcquireLabels: string[]): number => {
    gen += 1;
    lastAcquireGen = gen;
    pendingStickies.clear();
    pendingLabels.clear();
    toAcquireStickies.forEach(id => pendingStickies.add(id));
    toAcquireLabels.forEach(id => pendingLabels.add(id));
    return gen;
  };

  /** Bump gen and drop in-flight ids. Keep a queued persist (drop-then-deselect). */
  const invalidateInFlight = (): void => {
    gen += 1;
    pendingStickies.clear();
    pendingLabels.clear();
  };

  const discardQueuedPersist = (): void => {
    pendingPersist = null;
  };

  const applyAcquireResult = (
    requestGen: number,
    granted: boolean,
    selectedStickies: Set<string>,
    selectedLabels: Set<string>,
    wantStickies: string[],
    wantLabels: string[],
  ): AcquireApplyResult => {
    const isCurrent = requestGen === gen;
    // A stale reply must not clear a newer selection's in-flight ids.
    if (isCurrent) {
      pendingStickies.clear();
      pendingLabels.clear();
    }

    const persistBelongsToRequest = pendingPersist !== null && pendingPersistGen === requestGen;

    if (!granted) {
      if (persistBelongsToRequest) pendingPersist = null;
      return {
        granted: false,
        isCurrent,
        shouldAbortSession: requestGen === gen || requestGen === lastAcquireGen,
        releaseStickies: [],
        releaseLabels: [],
      };
    }

    const holdStickies = isCurrent ? wantStickies : wantStickies.filter(id => selectedStickies.has(id));
    const holdLabels = isCurrent ? wantLabels : wantLabels.filter(id => selectedLabels.has(id));
    holdStickies.forEach(id => heldStickies.add(id));
    holdLabels.forEach(id => heldLabels.add(id));

    if (persistBelongsToRequest) {
      const queued = pendingPersist;
      pendingPersist = null;
      queued?.();
    }

    return {
      granted: true,
      isCurrent,
      shouldAbortSession: false,
      releaseStickies: isCurrent ? [] : wantStickies.filter(id => !selectedStickies.has(id)),
      releaseLabels: isCurrent ? [] : wantLabels.filter(id => !selectedLabels.has(id)),
    };
  };

  return {
    heldStickies,
    heldLabels,
    isClaimed,
    claimed,
    persistWhenHeld,
    release,
    beginAcquire,
    invalidateInFlight,
    discardQueuedPersist,
    applyAcquireResult,
  };
}

function uniqueNonEmpty(ids: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(ids).filter(Boolean)));
}

export interface OccupancySelectionDiff {
  toReleaseStickies: string[];
  toReleaseLabels: string[];
  toAcquireStickies: string[];
  toAcquireLabels: string[];
}

/**
 * Diff currently held locks against the ids the local selection wants.
 * Acquire is the ids not yet held; release is held ids that dropped out of selection.
 */
export function diffOccupancySelection(
  heldStickies: Iterable<string>,
  heldLabels: Iterable<string>,
  wantStickies: Iterable<string>,
  wantLabels: Iterable<string>,
): OccupancySelectionDiff {
  const heldSList = uniqueNonEmpty(heldStickies);
  const heldLList = uniqueNonEmpty(heldLabels);
  const wantSList = uniqueNonEmpty(wantStickies);
  const wantLList = uniqueNonEmpty(wantLabels);
  const heldS = new Set(heldSList);
  const heldL = new Set(heldLList);
  const wantS = new Set(wantSList);
  const wantL = new Set(wantLList);
  return {
    toReleaseStickies: heldSList.filter(id => !wantS.has(id)),
    toReleaseLabels: heldLList.filter(id => !wantL.has(id)),
    toAcquireStickies: wantSList.filter(id => !heldS.has(id)),
    toAcquireLabels: wantLList.filter(id => !heldL.has(id)),
  };
}
